/**
 * =========================================================================
 * GOOGLE AUTHENTICATION BUTTON COMPONENT
 * =========================================================================
 * 
 * This component provides Google OAuth2 authentication for web and mobile apps.
 * It uses a direct redirect flow for maximum compatibility.
 * 
 * Features:
 * - Web and mobile app support
 * - Direct redirect OAuth flow (no popups)
 * - Loading states and error handling
 * - Platform detection and customization
 * 
 * Backend Integration:
 * - Uses POST /api/v1/auth/google/signin to get auth URL
 * - Backend handles OAuth callback and redirects back to frontend
 * - Supports custom mobile app schemes for deep linking
 * =========================================================================
 */

import React, { useState } from 'react';
// eslint-disable-next-line no-unused-vars
import { useAuth } from '../../../contexts/authContext';
import { useTranslation } from 'react-i18next';

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

/**
 * GoogleBtn Component
 * 
 * @param {Object} props - Component props
 * @param {string} props.text - Button text (default: "Continue with Google")
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.platform - Platform type: 'web', 'mobile', 'ios', 'android'
 * @param {string} props.mobileAppScheme - Deep link scheme for mobile apps (e.g., 'myapp://auth/callback')
 * @param {string} props.deviceId - Unique device identifier for mobile apps
 * @param {Function} props.onSuccess - Callback on successful authentication
 * @param {Function} props.onError - Callback on authentication error
 */
const GoogleBtn = ({ 
  text = "Continue with Google", 
  className = "",
  platform = 'web',
  mobileAppScheme = null,
  deviceId = null,
  onSuccess,
  onError 
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /**
   * Handle Google Authentication Flow
   * Supports both web and mobile platforms with direct redirect
   */
  const handleGoogleAuth = async () => {
    setLoading(true);
    setError('');

    try {
      // Prepare platform-specific request data
      const requestData = {
        frontendUrl: window.location.origin,
        platform: platform,
        ...(platform !== 'web' && mobileAppScheme && { mobileAppScheme }),
        ...(platform !== 'web' && deviceId && { deviceId })
      };

      // Get Google OAuth URL from backend
      const response = await fetch(`${API_URL}/auth/google/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`Failed to get Google auth URL: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status !== 'success' || !data.authUrl) {
        throw new Error(data.errors?.[0] || 'Failed to get Google authentication URL');
      }

      // Direct redirect to Google OAuth
      window.location.href = data.authUrl;

    } catch (error) {
      console.error('Google authentication error:', error);
      setError(error.message || 'Google authentication failed');
      setLoading(false);
      
      if (onError) {
        onError(error);
      }
    }
  };

  return (
    <div className="w-full flex flex-col items-center justify-center">
      <button
        onClick={handleGoogleAuth}
        disabled={loading}
        style={{padding:"10px"}}
        className={`
          relative w-full flex items-center justify-center gap-3
          bg-white hover:bg-gray-50 
          border-2 border-gray-300 hover:border-gray-400
          text-gray-700 font-semibold text-lg
          py-3 px-6 rounded-lg
          transition-all duration-200
          shadow-md hover:shadow-lg
          disabled:opacity-50 disabled:cursor-not-allowed
          disabled:hover:bg-white disabled:hover:border-gray-300
          ${className}
        `}
      >
        {loading ? (
          <>
            {/* Loading Spinner */}
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
            <span>Authenticating...</span>
          </>
        ) : (
          <>
            {/* Google Icon */}
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span >{text}</span>
          </>
        )}
      </button>

      {/* Error Message */}
      {error && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
          <button
            onClick={() => setError('')}
            className="mt-1 text-xs text-red-500 hover:text-red-700 underline"
          >
{t("buttons.dismiss")}
          </button>
        </div>
      )}

      {/* Instructions */}
      <p style={{margin:"20px",lineHeight:"25px"}} className="mt-2 text-xs text-gray-500 text-center">
{t("buttons.byProceedingAgree")}
      </p>
    </div>
  );
};

export default GoogleBtn;
